import React, { Fragment, useState, useEffect } from "react"
import { graphql, withAssetPrefix } from "gatsby"
import { MDXProvider } from "@mdx-js/react"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { ThemeProvider } from "styled-components"
import { themeDefault, H1, H4, H5, H6, OnThisPage, hooks } from "@fastly/consistently"
import { ConsistentlyProvider } from "@fastly/consistently-vanilla"
import { themeConsistentlyLightV3 } from "@fastly/consistently-vanilla/tokens"
import classNames from "clsx"

// Common layout components
import Header from "~components/header/header"
import Footer from "~components/footer/footer"
import Sidebar from "~components/sidebar/sidebar"
import SEO from "~components/seo/seo"
import Breadcrumbs from "~components/breadcrumbs/breadcrumbs"
import Pagination from "~components/pagination/pagination"
import TabbedPanels from "~components/tabbed-panels/tabbed-panels"
import Columns from "~components/columns/columns"
import ToolTip from "~components/tooltip/tooltip"

// Navigation components
import useNavigationFields from "~hooks/navigation-fields"
import useNavigationTree from "~hooks/navigation-tree"
import VclTokensProvider from "~providers/vcl-tokens"
import HeadersProvider from "~providers/headers"
import CLICommandsProvider from "~providers/cli-commands"
import TermsProvider from "~providers/terms"

// Common MDX components
import Partial from "~components/partial/partial"
import extractFlag from "~utils/extract-mdx-flag"
import Aside from "~components/aside/aside"
import Link from "~components/link/link"
import TokenLink from "~components/token-link/token-link"
import FluidImage from "~components/fluid-image/fluid-image"
import PlainTextProvider from "~providers/plaintext"
import { CodeBlock } from "@fastly/consistently"
import EditPageButton from "~components/edit-page-button/edit-page-button"
import Icon from "~components/icon/icon"
import Video from "~components/video/video"

// Cards
import { Card } from "@fastly/consistently"
import CardGallery from "~components/card/card-gallery"
// Lists
import ColumnsList from "~components/columns-list/columns-list"
// Fiddle
import Fiddle from "~components/fiddle/fiddle"
import Comments from "~components/comments/comments"
// SDK version warnings
import SDKWarnings from "~components/sdk-warnings/sdk-warnings"

// Preferences
import { PreferencesProvider } from "~context/preferences"

// Styles
import * as styles from "./mdx.module.css"

const Code = ({ src, ...props }) => {
  if (!props.language && !props.children.props.className) {
    props.language = "plain"
  }
  return src && src.length ? (
    <PlainTextProvider
      src={src}
      render={(content) => (
        <CodeBlock {...props}>
          {content || `The source ${src} was not found (does it start with src/content/ ?)`}
        </CodeBlock>
      )}
    />
  ) : (
    <CodeBlock {...props} />
  )
}

const commonComponents = {
  pre: Code,
  a: ({ href: to, ...props }) =>
    to && to.startsWith("/reference/glossary#") ? (
      <TermsProvider
        slug={to.slice("/reference/glossary#".length)}
        render={(term) =>
          term ? (
            <Link to={to} data-glossary-tip={term.definitions[0].plain} className={styles.linkGlossary} {...props} />
          ) : (
            <span {...props} />
          )
        }
      />
    ) : to ? (
      <Link to={to} {...props} />
    ) : (
      <a {...props} /> // eslint-disable-line jsx-a11y/anchor-has-content
    ),
  Link,
  ol: (props) => <ColumnsList ordered={true} {...props} />,
  ul: (props) => <ColumnsList ordered={false} {...props} />,
  Aside,
  blockquote: (props) => {
    const flag = extractFlag(props)
    return flag ? (
      <Aside type={flag.type} title={flag.title}>
        {props.children}
      </Aside>
    ) : (
      <blockquote>{props.children}</blockquote>
    )
  },
  table: (props) => (
    <div className="tableWrapper">
      <table {...props} />
    </div>
  ),
  img: (props) => <FluidImage relativePath={props.src} alt={props.alt} />,
  h2: ({ children, ...props }) => (
    <H4 as="h2" {...props}>
      {children}
    </H4>
  ),
  h3: ({ children, ...props }) => (
    <H5 as="h3" {...props}>
      {children}
    </H5>
  ),
  h4: ({ children, ...props }) => (
    <H6 as="h4" {...props}>
      {children}
    </H6>
  ),
  Partial,
  Fragment,
  ColumnsList,
  TabbedPanels,
  Card,
  CardGallery,
  Fiddle,
  FluidImage,
  CodeBlock: Code,
  Columns,
  Icon,
  Video,
}

const SidebarContainer = ({ children, withWrapper = false }) => {
  if (withWrapper) {
    return <div className={styles.sidebarContainer}>{children}</div>
  }
  return children
}

const DefaultTemplate = ({ children }) => <>{children}</>

const MDXWrapper = ({
  data,
  pageContext,
  location,
  components = {},
  Template = DefaultTemplate,
  sidebarWidgets = pageContext.sidebarWidgets,
}) => {
  const { useIntersectionObserver } = hooks
  const { containerRef, activeEl } = useIntersectionObserver()

  const page = { path: location.pathname, ...pageContext }
  Object.assign(components, commonComponents)

  // Theme setup
  const theme = themeDefault

  // Sidebar setup
  const { navTree, extractTree, breadcrumbs, childrenOfPath } = useNavigationTree(location.pathname)
  if (!sidebarWidgets) sidebarWidgets = sidebarWidgets === null ? [] : ["navTree"]

  const withTOC =
    page.withTOC !== undefined
      ? Boolean(page.withTOC)
      : data?.mdx?.headings?.length > 2 && data?.mdx?.rawBody?.length > 6000

  const withBreadcrumbs = page.withBreadcrumbs !== false

  // The useNavigationFields hook cannot be called conditionally
  // React Hooks must be called in the exact same order in every component render
  // see: https://reactjs.org/docs/hooks-rules.html#explanation
  const sidebarNodes = {
    navTree: extractTree(navTree, (n) => !!n.sidebarRoot) || navTree,
    affectedSystems: useNavigationFields("affectedSystems", location.pathname + location.hash),
    changeLabels: useNavigationFields("changeLabels", location.pathname),
    recentChanges: useNavigationFields("recentChanges", location.pathname),
  }

  components.ChildPagesIndex = ({ filter = (n) => true, basic = false, renderItem }) => {
    // Index pages where all children are unlisted are automatically excluded from the sidebar
    // However, we should still display children if such a page is requested by its URL
    const children = childrenOfPath(navTree, page.path)
    const listedChildren = children?.filter((n) => !n.unlisted)
    if (basic || renderItem) {
      return (
        <ColumnsList maxCols={2}>
          {(listedChildren?.length ? listedChildren : children)?.filter(filter).map((n) => (
            <li key={n.path}>{renderItem ? renderItem(n) : <Link to={n.path}>{n.title}</Link>}</li>
          ))}
        </ColumnsList>
      )
    }

    return (
      <CardGallery
        data={listedChildren.map((p) => ({
          title: p.title,
          content: p.summary ? <div dangerouslySetInnerHTML={{ __html: p.summary }} /> : null,
          link: p.path,
        }))}
      />
    )
  }

  components.inlineCode = ({ children }) => (
    <VclTokensProvider
      query={children}
      render={(t) =>
        t && t.path !== page.path ? (
          <TokenLink {...t} />
        ) : (
          <HeadersProvider
            query={children}
            render={(h) => (h && h.path !== page.path ? <TokenLink type="header" {...h} /> : <code>{children}</code>)}
          />
        )
      }
    />
  )
  components.em = ({ children }) =>
    children === "not set" ? (
      <TokenLink type="notset" path="/reference/vcl/types/#not-set" name="not set" />
    ) : (
      <em>{children}</em>
    )
  components.kbd = ({ children }) => (
    <CLICommandsProvider
      query={children}
      render={(c) =>
        c && c.path !== page.path ? (
          <kbd>
            <Link to={c.path}>{c.name}</Link>
          </kbd>
        ) : (
          <kbd>{children}</kbd>
        )
      }
    />
  )

  const hasChildren = childrenOfPath(navTree, page.path)?.length !== 0
  const breadcrumbTrail = breadcrumbs.filter((p) => !/(\/p\d+\/?)$/.test(p.path))
  // Comments disabled until Discourse traffic can be reduced (DEVLIB-1414)
  // const withComments = page.withComments !== undefined ? Boolean(page.withComments) : !hasChildren
  const withComments = false

  return (
    <ConsistentlyProvider theme={themeConsistentlyLightV3}>
      <ThemeProvider theme={theme}>
        <PreferencesProvider>
          {page.title && (
            <SEO
              title={page.title}
              label={page.label}
              unlisted={page.unlisted}
              excludeFromSearch={page.excludeFromSearch}
              description={page.description || page.summary}
              path={page.path}
            />
          )}
          <Header />
          <main
            className={classNames(styles.container, {
              [styles.withSidebar]: sidebarWidgets.length,
              [styles.withTOC]: withTOC,
            })}
          >
            {sidebarWidgets.length > 0 && (
              <SidebarContainer withWrapper={sidebarWidgets.length > 1}>
                {sidebarWidgets.map(
                  (widget) =>
                    sidebarNodes[widget].children.some(
                      (node) => node.path || node.externalUrl || node.children.length
                    ) && <Sidebar key={`sidebar-${widget}`} rootNode={sidebarNodes[widget]} />
                )}
              </SidebarContainer>
            )}
            <section className={styles.content} ref={containerRef}>
              {withBreadcrumbs && <Breadcrumbs path={breadcrumbTrail} />}
              {page.editUrl && <EditPageButton url={page.editUrl} float={true} />}
              <div className={styles.header}>
                {page.icon && <img src={withAssetPrefix(page.icon)} alt="" className={styles.icon} />}
                {(page.title || page.summary) && (
                  <div className={styles.title}>
                    {page.title && (
                      <H1>
                        {page.title}
                        {page.label && `: ${page.label}`}
                      </H1>
                    )}
                    {!page.hideSummary && page.summary && (
                      <p className={styles.summary} dangerouslySetInnerHTML={{ __html: page.summary }}></p>
                    )}
                  </div>
                )}
              </div>
              <SDKWarnings sdks={page.sdks} />
              <MDXProvider components={components}>
                <Template data={data} {...page}>
                  {data && data.mdx && <MDXRenderer {...page}>{data.mdx.body}</MDXRenderer>}
                </Template>
              </MDXProvider>
              {page.paginate && <Pagination currentPage={page.currentPage} numPages={page.numPages} path={page.path} />}
              {withComments && <Comments path={page.path} />}
              {page.editUrl && <EditPageButton url={page.editUrl} />}
            </section>
            {withTOC && data.mdx.tableOfContents && data.mdx.tableOfContents.items && (
              <nav className={styles.onThisPage}>
                <div>
                  <OnThisPage label={`On this page`} navContent={data.mdx.tableOfContents.items} activeEl={activeEl} />
                </div>
              </nav>
            )}
          </main>
          <Footer />
          <ToolTip
            target="[data-glossary-tip]"
            contentAttr="data-glossary-tip"
            trigger="hover"
            placement="bottom"
            placementAttr="data-tip-placement"
            type="definition"
          />
          <ToolTip target="[data-tip]" contentAttr="data-tip" trigger="hover" />
          <ToolTip target=".anchor" trigger="hover">
            Link to this heading
          </ToolTip>
        </PreferencesProvider>
      </ThemeProvider>
    </ConsistentlyProvider>
  )
}

export const query = graphql`
  query ($mdxPath: String) {
    mdx(fileAbsolutePath: { eq: $mdxPath }) {
      body
      tableOfContents(maxDepth: 10)
      headings {
        depth
      }
      rawBody
    }
  }
`

export default MDXWrapper
